import { useRef, useState } from "react";
import "./RoomInspirationCollage.css";

const ROOMS = [
  { name: "Drain", image: "/media/inspiration/drain.jpg", alt: "Drain bunks with wood trim and black pipe rails" },
  { name: "Ernst", image: "/media/inspiration/ernst.jpg", alt: "Ernst floating bunks with a central custom staircase" },
  { name: "Goulding", image: "/media/inspiration/goulding.jpg", alt: "Goulding corner bunks with coastal striped bedding" },
  { name: "Johnson", image: "/media/inspiration/johnson.jpg", alt: "Johnson bunk room with colorful bedding and stair access" },
] as const;

function JohnsonSchematic() {
  return <span className="bcj-johnson-schematic">
    <svg viewBox="0 0 220 180" role="img" aria-label="Overhead: perpendicular Queen over Queen, side staircase and built-in shelf">
      <g fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="55" y="15" width="130" height="97.5" />
        <rect x="58" y="18" width="91.5" height="124" strokeDasharray="3 4" />
        <path d="M20 15H55V145H20ZM20 34H55M20 53H55M20 72H55M20 91H55M20 110H55M20 129H55" />
        <rect x="152.5" y="15" width="32.5" height="97.5" />
      </g>
    </svg>
    <span>Johnson</span>
    <small>Single, Perpendicular Queen over Queen<br />Side Staircase · Built-in Shelf</small>
    <small>Solid: upper · Dotted: lower<br />Right-hand unit</small>
  </span>;
}

export default function RoomInspirationCollage() {
  const dialog = useRef<HTMLDialogElement>(null);
  const [selected, setSelected] = useState<(typeof ROOMS)[number]>(ROOMS[0]);
  return (
    <section className="bcj-inspiration" aria-label="Popular build inspiration">
      <h2>A little room inspiration</h2>
      <p>Explore a few more ways to bring everyone together.</p>
      <div className="bcj-inspiration-grid">
        {ROOMS.map((room) => (
          <figure key={room.name}>
            <button type="button" aria-label={`Enlarge ${room.name} build`} onClick={() => {
              setSelected(room);
              dialog.current?.showModal();
            }}>
              <img src={room.image} alt={room.alt} loading="lazy" width="600" height="600" />
              {(room.name === "Drain" || room.name === "Ernst") && <img className="bcj-inspiration-monogram" src="/bliss-bedrooms-monogram-transparent.png" alt="" aria-hidden="true" />}
              {room.name === "Johnson" && <JohnsonSchematic />}
            </button>
            <figcaption>{room.name}</figcaption>
          </figure>
        ))}
      </div>

      <details className="bcj-johnson-reviews" id="johnson-proof">
        <summary>Proof · The Johnson Case Study</summary>
        <h3>One Room. More Possibilities.</h3>
        <p>At Beaches Be Crazy in Destin, our Johnson bunk-room buildout brings six queen beds together in a former garage. It gives families a shared place to sleep, gather and make memories—and guests repeatedly mention it in their reviews.</p>
        <p><strong>Up to 12 guests in the bunk room · 21 advertised for the home</strong><br />With two people sharing each queen bed, this one room can accommodate approximately 57% of the home’s advertised guest capacity.</p>
        <p><strong>9.8/10 · 54 Vrbo reviews</strong><br />Overall property rating shown in the owner-supplied Vrbo screenshot, October 24, 2026. Vrbo also displays “Top 10% of guest reviews in this area.”</p>
        <h3>Guests remember the room.</h3>
        <p>Selected excerpts from rental guests describe the bunk room’s comfort, character and appeal for children and teenagers.</p>
        <blockquote>
          <p>“The kid’s bunk room is unlike anything we’ve ever seen.”</p>
          <cite>Ryan S. · Vrbo guest review</cite>
        </blockquote>
        <blockquote>
          <p>“The game area was fun and bunk room was perfect for all the kids.”</p>
          <cite>Eric B. · May 2024 stay · Vrbo</cite>
        </blockquote>
        <blockquote>
          <p>“The kids' bunk room was perfect for the ultimate sleepover”</p>
          <cite>Lisa T. · October 2024 stay · Vrbo · excerpt</cite>
        </blockquote>
        <blockquote>
          <p>“Great house, the downstairs bunk room was fabulous for multiple teenage children.”</p>
          <cite>Kerrie F. · June 2025 stay · Vrbo</cite>
        </blockquote>
        <blockquote>
          <p>“The teenagers thoroughly enjoyed the bunk room and game room.”</p>
          <p>“I would definitely stay again.”</p>
          <cite>Karen H. · April 2026 stay · Vrbo · excerpts from the same review</cite>
        </blockquote>
        <h3>Praise within an honest review.</h3>
        <blockquote>
          <p>“a beautiful bunk room”</p>
          <cite>Adam D. · June 2025 stay · 8/10 overall property review · Vrbo · excerpt</cite>
        </blockquote>
        <p>Adam praised the bunk room while explaining that the home’s multi-floor layout, stairs and distance from younger children were inconvenient for his family. Bliss built the bunk room; the home’s overall floor arrangement was outside our scope.</p>
        <h3>Room for a better family stay.</h3>
        <p>The capacity figures show how much sleeping space this room provides. The guest comments show that the experience matters, too. Karen’s review pairs enjoyment of the bunk and game rooms with a desire to stay again—an expression of return interest, not a confirmed repeat booking.</p>
        <p>The rating covers the entire rental property. These selected reviews document guest experience; they do not measure a revenue increase or assign a share of the rating to the bunk room.</p>
        <a href="https://www.vrbo.com/3749828" target="_blank" rel="noopener noreferrer">Read the property’s guest reviews on Vrbo ↗</a>
      </details>
      <p className="bcj-inspiration-note">Custom build inspiration. Your layout and fit are confirmed for your room.</p>
      <dialog ref={dialog} className="bcj-inspiration-dialog" aria-label={`${selected.name} build`} onClick={(event) => {
        if (event.target === event.currentTarget) dialog.current?.close();
      }}>
        <button type="button" autoFocus onClick={() => dialog.current?.close()} aria-label="Close enlarged photo">Close ×</button>
        <div className="bcj-inspiration-expanded-photo">
          <img src={selected.image} alt={selected.alt} />
          {selected.name === "Johnson" && <JohnsonSchematic />}
        </div>
        <p>{selected.name}</p>
      </dialog>
    </section>
  );
}
